import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Play, Volume2, Maximize, Settings, Radio, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function EmbedPlayer() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    api.get(`/public/players/${id}`).then((r) => setP(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="fixed inset-0 bg-black flex items-center justify-center"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;
  }
  if (error || !p) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-center px-6">
        <AlertTriangle className="h-8 w-8 text-amber-400" />
        <p className="mt-3 text-white font-medium">Player unavailable</p>
        <p className="text-gray-500 text-sm mt-1">This player may have been removed.</p>
      </div>
    );
  }

  const ap = p.appearance || {};
  const s = p.settings || {};
  const org = p.org || {};
  const accent = ap.bar_background || org.accent_color || "#F59E0B";
  const logo = ap.logo_overlay_url || org.logo_url || "";
  const autoplay = s.autoplay && p.active;

  return (
    <div className="fixed inset-0 bg-black flex flex-col select-none">
      {/* Branding bar */}
      {(ap.playlist_bar_label || org.organization) ? (
        <div className="flex items-center gap-2 px-4 py-2 text-sm font-semibold" style={{ background: accent, color: ap.bar_text_color || "#0B0F17" }}>
          {logo ? <img src={logo} alt="logo" className="h-5 object-contain" /> : <Radio className="h-4 w-4" />} {ap.playlist_bar_label || org.organization}
        </div>
      ) : null}

      {/* Video area */}
      <div className="relative flex-1 bg-grid flex items-center justify-center overflow-hidden">
        {logo ? (
          <img src={logo} alt="logo" className="absolute top-3 right-3 h-8 opacity-90 object-contain z-10" />
        ) : null}

        {!p.active ? (
          <div className="text-center">
            <p className="text-xl font-semibold text-white">Player is disabled</p>
          </div>
        ) : playing || autoplay ? (
          <div className="text-center">
            <div className="h-16 w-16 rounded-full border flex items-center justify-center mx-auto animate-pulse" style={{ borderColor: accent + "66" }}>
              <Volume2 className="h-7 w-7" style={{ color: accent }} />
            </div>
            <p className="mt-4 text-gray-300 text-sm">Playing preview…</p>
            {s.show_title && <p className="mt-1 text-white font-medium">{p.name}</p>}
          </div>
        ) : (
          <button
            data-testid="embed-play-button"
            onClick={() => setPlaying(true)}
            className="group flex flex-col items-center cursor-pointer"
          >
            <span className="h-20 w-20 rounded-full flex items-center justify-center shadow-2xl transition-transform group-hover:scale-110" style={{ background: accent, color: ap.bar_text_color || "#0B0F17" }}>
              <Play className="h-8 w-8 ml-1" fill="currentColor" />
            </span>
            {s.show_title && <span className="mt-4 text-white font-medium">{p.name}</span>}
          </button>
        )}
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-4 px-4 py-2.5 bg-black/90 border-t border-gray-800 text-gray-300">
        <button onClick={() => setPlaying((v) => !v)} className="hover:text-white"><Play className="h-4 w-4" fill="currentColor" /></button>
        <div className="h-1 flex-1 rounded-full bg-gray-700 overflow-hidden"><div className="h-full w-1/3" style={{ background: accent }} /></div>
        <Volume2 className="h-4 w-4" />
        {s.allow_playback_speed && <span className="text-xs font-medium">1x</span>}
        {s.allow_fullscreen && <Maximize className="h-4 w-4" />}
        <Settings className="h-4 w-4" />
      </div>
    </div>
  );
}
